/**
 * Shared Asset Editor — Patch 28.
 *
 * The ONE editor used by every consumer surface: Content Review,
 * Content Vault, Campaign Room, Publishing Queue, Calendar, Asset
 * Detail. There are no duplicates. Adding a new consuming page is
 * one import.
 *
 * Executive rules enforced here:
 *   • Editable across ALL statuses (draft → published) — Part I
 *   • AI rewrites start from the OWNER's latest edit — Part IV
 *   • Version history is preserved for restore & diff — Part III
 *   • Published edits present two explicit choices — Part VI:
 *       "Save as Revision" (draft) or "Republish Now"
 *   • Marketing Intelligence Report exposes the Blueprint,
 *     never a raw provider prompt — executive amendment
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Brain, Check, ChevronDown, History, Loader2, RotateCcw, Save, Send,
  Sparkles, Wand2,
} from "lucide-react";
import { toast } from "sonner";

import {
  aiRewrite,
  fetchMarketingIntelligence,
  listRevisions,
  republish,
  restoreRevision,
  REWRITE_GROUPS,
  REWRITE_LABEL,
  saveEdit,
  type AssetRevision,
  type MarketingIntelligenceReport,
  type RewriteAction,
} from "@/entities/revision";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/shared/ui/sheet";
import { Skeleton } from "@/shared/ui/skeleton";
import { Textarea } from "@/shared/ui/textarea";
import { PublishToPanel } from "@/features/publish-to-panel";


type Status = "draft" | "needs_review" | "approved" | "scheduled" | "published" | string;


export interface AssetEditorProps {
  workspaceId: string;
  permanentAssetId: string;
  initialBody: string;
  initialStatus: Status;
  initialTitle?: string;
  /** Called after any successful save so the parent page can invalidate. */
  onSaved?: (revision: AssetRevision) => void;
  /** Optional label shown above the editor. */
  contextLabel?: string;
}


export function AssetEditor({
  workspaceId, permanentAssetId, initialBody, initialStatus,
  initialTitle, onSaved, contextLabel,
}: AssetEditorProps): JSX.Element {
  const qc = useQueryClient();
  const [body, setBody] = useState(initialBody);
  const [title, setTitle] = useState(initialTitle ?? "");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [intelligenceOpen, setIntelligenceOpen] = useState(false);

  // Reset local state when the parent switches to a different asset.
  useEffect(() => { setBody(initialBody); }, [initialBody, permanentAssetId]);
  useEffect(() => { setTitle(initialTitle ?? ""); }, [initialTitle, permanentAssetId]);

  const isPublished = initialStatus === "published";
  const isEditable = ["draft", "needs_review", "approved", "scheduled", "published", "queued", "publish_failed"].includes(initialStatus);
  const isDirty = body !== initialBody || (initialTitle !== undefined && title !== initialTitle);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["asset-revisions", workspaceId, permanentAssetId] });
    qc.invalidateQueries({ queryKey: ["ai-assets"] });
    qc.invalidateQueries({ queryKey: ["assets"] });
    qc.invalidateQueries({ queryKey: ["content-review"] });
    qc.invalidateQueries({ queryKey: ["publishing"] });
  };

  const saveMutation = useMutation({
    mutationFn: () => saveEdit(workspaceId, permanentAssetId, { body_markdown: body, title: title || undefined }),
    onSuccess: (r) => {
      toast.success(isPublished ? "Saved as a draft revision. Republish when you're ready." : `Saved v${r.version}`);
      invalidate(); onSaved?.(r);
    },
    onError: (e: Error) => toast.error(e.message || "Save failed"),
  });

  const republishMutation = useMutation({
    mutationFn: () => republish(workspaceId, permanentAssetId, body),
    onSuccess: (r) => {
      toast.success(`Republished v${r.version}`);
      invalidate(); onSaved?.(r);
    },
    onError: (e: Error) => toast.error(e.message || "Republish failed"),
  });

  const rewriteMutation = useMutation({
    mutationFn: (action: RewriteAction) => aiRewrite(workspaceId, permanentAssetId, action),
    onSuccess: (r) => {
      setBody(r.revision.body_markdown ?? body);
      toast.success(`${REWRITE_LABEL[r.ai_action]} applied — review and save.`);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message || "Rewrite failed"),
  });

  return (
    <div data-testid={`asset-editor-${permanentAssetId}`} className="space-y-3">
      {contextLabel && (
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {contextLabel}
        </div>
      )}

      {initialTitle !== undefined && (
        <input
          data-testid="asset-editor-title"
          type="text"
          value={title}
          disabled={!isEditable}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full rounded-md border border-border/60 bg-card/60 px-3 py-2 font-display text-lg outline-none focus:border-emerald-500/60"
          placeholder="Asset title"
        />
      )}

      <Textarea
        data-testid="asset-editor-textarea"
        value={body}
        disabled={!isEditable}
        onChange={(e) => setBody(e.target.value)}
        rows={10}
        className="min-h-[200px] font-mono text-sm leading-6"
        placeholder="Write here…"
      />

      <div className="flex flex-wrap items-center gap-2">
        {!isPublished ? (
          <Button
            data-testid="asset-editor-save"
            onClick={() => saveMutation.mutate()}
            disabled={!isDirty || saveMutation.isPending || !isEditable}
            size="sm"
          >
            {saveMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save
          </Button>
        ) : (
          <>
            <Button
              data-testid="asset-editor-save-as-revision"
              onClick={() => saveMutation.mutate()}
              disabled={!isDirty || saveMutation.isPending}
              size="sm"
              variant="outline"
            >
              {saveMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save as Revision
            </Button>
            <Button
              data-testid="asset-editor-republish"
              onClick={() => { if (confirm("Republish this edited version live now?")) republishMutation.mutate(); }}
              disabled={!isDirty || republishMutation.isPending}
              size="sm"
            >
              {republishMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Republish Now
            </Button>
          </>
        )}

        <RewriteMenu
          disabled={!isEditable || rewriteMutation.isPending}
          pending={rewriteMutation.isPending}
          onPick={(action) => rewriteMutation.mutate(action)}
        />

        <Button
          data-testid="asset-editor-history-button"
          size="sm"
          variant="outline"
          onClick={() => setHistoryOpen(true)}
        >
          <History className="mr-2 h-4 w-4" />
          History
        </Button>

        <Button
          data-testid="asset-editor-intelligence-button"
          size="sm"
          variant="outline"
          onClick={() => setIntelligenceOpen(true)}
        >
          <Brain className="mr-2 h-4 w-4" />
          Marketing Intelligence
        </Button>

        {isPublished && (
          <Badge variant="outline" className="ml-auto text-[10px] uppercase tracking-widest text-amber-300 border-amber-500/40">
            Live · edits create revisions
          </Badge>
        )}
      </div>

      <VersionHistoryDrawer
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        workspaceId={workspaceId}
        permanentAssetId={permanentAssetId}
        currentBody={body}
        onRestore={(newBody) => { setBody(newBody); invalidate(); onSaved?.({} as AssetRevision); }}
      />

      <MarketingIntelligenceDialog
        open={intelligenceOpen}
        onOpenChange={setIntelligenceOpen}
        workspaceId={workspaceId}
        permanentAssetId={permanentAssetId}
      />

      {/* Patch 33.2.1 — provider-neutral Publish To panel. Draft /
          Publish Now / Schedule via PublishingEngine. */}
      <PublishToPanel workspaceId={workspaceId} assetId={permanentAssetId} body={body} disabled={!isEditable} />
    </div>
  );
}


function RewriteMenu({ disabled, pending, onPick }: { disabled: boolean; pending: boolean; onPick: (a: RewriteAction) => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid="asset-editor-rewrite-trigger"
          size="sm"
          variant="outline"
          disabled={disabled}
        >
          {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
          AI Rewrite
          <ChevronDown className="ml-1 h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56" data-testid="asset-editor-rewrite-menu">
        {REWRITE_GROUPS.map((group, idx) => (
          <div key={group.label}>
            {idx > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {group.label}
            </DropdownMenuLabel>
            <DropdownMenuGroup>
              {group.actions.map((a) => (
                <DropdownMenuItem
                  key={a}
                  data-testid={`asset-editor-rewrite-${a}`}
                  onSelect={() => onPick(a)}
                  className="text-sm"
                >
                  {REWRITE_LABEL[a]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}


function VersionHistoryDrawer({
  open, onOpenChange, workspaceId, permanentAssetId, currentBody, onRestore,
}: {
  open: boolean; onOpenChange: (v: boolean) => void;
  workspaceId: string; permanentAssetId: string;
  currentBody: string; onRestore: (newBody: string) => void;
}) {
  const q = useQuery({
    queryKey: ["asset-revisions", workspaceId, permanentAssetId],
    queryFn: () => listRevisions(workspaceId, permanentAssetId),
    enabled: open,
  });

  const restoreMutation = useMutation({
    mutationFn: (revisionId: string) => restoreRevision(workspaceId, permanentAssetId, revisionId),
    onSuccess: (r) => {
      toast.success(`Restored — created v${r.version}`);
      onRestore(r.body_markdown ?? currentBody);
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg" data-testid="asset-editor-history-drawer">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            <History className="h-5 w-5" /> Version history
          </SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-3">
          {q.isLoading && <><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /></>}
          {q.data && q.data.length === 0 && (
            <div className="text-sm text-muted-foreground">No history yet — save an edit to start the timeline.</div>
          )}
          {(q.data ?? []).map((rev) => (
            <div
              key={rev.revision_id}
              className={`rounded-md border p-3 ${rev.is_current ? "border-emerald-500/40 bg-emerald-500/5" : "border-border/60 bg-card/60"}`}
              data-testid={`asset-editor-history-item-${rev.revision_id}`}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  v{rev.version} · {rev.action.replace("_", " ")}
                </span>
                <Badge variant="outline" className="text-[10px]">{rev.author}</Badge>
                {rev.is_current && (
                  <Badge className="text-[10px]" variant="outline">
                    <Check className="mr-1 h-3 w-3" /> Current
                  </Badge>
                )}
                <span className="ml-auto text-[10px] text-muted-foreground">
                  {new Date(rev.created_at).toLocaleString()}
                </span>
              </div>
              {rev.ai_notes && (
                <div className="mt-1 text-[11px] text-muted-foreground italic">{rev.ai_notes}</div>
              )}
              <p className="mt-2 text-xs whitespace-pre-wrap line-clamp-4">
                {rev.body_markdown ?? "(no body)"}
              </p>
              {!rev.is_current && (
                <div className="mt-2 flex justify-end">
                  <Button
                    data-testid={`asset-editor-restore-${rev.revision_id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => restoreMutation.mutate(rev.revision_id)}
                    disabled={restoreMutation.isPending}
                  >
                    <RotateCcw className="mr-2 h-3 w-3" />
                    Restore
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}


function MarketingIntelligenceDialog({
  open, onOpenChange, workspaceId, permanentAssetId,
}: {
  open: boolean; onOpenChange: (v: boolean) => void;
  workspaceId: string; permanentAssetId: string;
}) {
  const q = useQuery({
    queryKey: ["mktintel", workspaceId, permanentAssetId],
    queryFn: () => fetchMarketingIntelligence(workspaceId, permanentAssetId),
    enabled: open,
    retry: false,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="marketing-intelligence-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Brain className="h-5 w-5 text-emerald-500" /> Marketing Intelligence Report
          </DialogTitle>
        </DialogHeader>
        {q.isLoading && <><Skeleton className="h-24 w-full" /><Skeleton className="h-24 w-full" /></>}
        {q.isError && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-4 text-xs text-amber-100" data-testid="marketing-intelligence-empty">
            This asset was generated before Patch 27's Marketing Blueprint went live, so it has no
            blueprint attached yet. Generate a new version to see the full strategy behind the piece.
          </div>
        )}
        {q.data && <IntelligenceReportBody report={q.data} />}
      </DialogContent>
    </Dialog>
  );
}


function IntelligenceReportBody({ report }: { report: MarketingIntelligenceReport }) {
  const audienceLines = useMemo(() => report.target_audience.filter(Boolean), [report.target_audience]);
  return (
    <div className="space-y-4 text-sm" data-testid="marketing-intelligence-body">
      <Section label="Objective">
        <p className="text-foreground/90">{report.objective}</p>
      </Section>
      {audienceLines.length > 0 && (
        <Section label="Target Audience">
          <BulletList items={audienceLines} testId="mi-audience" />
        </Section>
      )}
      {report.emotional_triggers.length > 0 && (
        <Section label="Emotional Trigger">
          <div className="flex flex-wrap gap-2" data-testid="mi-emotions">
            {report.emotional_triggers.map((t) => (
              <Badge key={t} variant="outline" className="text-[11px]">{t}</Badge>
            ))}
          </div>
        </Section>
      )}
      {report.primary_pains.length > 0 && (
        <Section label="Primary Pain">
          <BulletList items={report.primary_pains} testId="mi-pains" />
        </Section>
      )}
      {(report.offer.what || report.offer.why_now || report.offer.risk_reversal) && (
        <Section label="Offer">
          <Card className="border-border/60 bg-card/60">
            <CardContent className="space-y-1 py-3 text-xs">
              {report.offer.what && <div><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">What:</span> {report.offer.what}</div>}
              {report.offer.why_now && <div><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Why now:</span> {report.offer.why_now}</div>}
              {report.offer.risk_reversal && <div><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Guarantee:</span> {report.offer.risk_reversal}</div>}
            </CardContent>
          </Card>
        </Section>
      )}
      <Section label="Call to Action">
        <p className="text-foreground/90">{report.cta}</p>
      </Section>
      {report.brand_voice.length > 0 && (
        <Section label="Brand Voice">
          <div className="flex flex-wrap gap-2" data-testid="mi-voice">
            {report.brand_voice.map((v) => (
              <Badge key={v} variant="outline" className="text-[11px]">{v}</Badge>
            ))}
          </div>
        </Section>
      )}
      {report.creative_director_notes.length > 0 && (
        <Section label="Creative Director Notes">
          <ul className="space-y-1 text-xs text-foreground/85" data-testid="mi-cd-notes">
            {report.creative_director_notes.map((n, i) => (
              <li key={i} className="flex gap-2">
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
                <span>{n}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}
      <div className="mt-4 border-t border-border/40 pt-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        Blueprint v{report.blueprint_reference.version} · {report.blueprint_reference.asset_type} · {report.blueprint_reference.target_platform}
      </div>
    </div>
  );
}


function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}


function BulletList({ items, testId }: { items: string[]; testId?: string }) {
  return (
    <ul className="list-disc space-y-0.5 pl-5 text-xs text-foreground/85" data-testid={testId}>
      {items.map((s, i) => <li key={i}>{s}</li>)}
    </ul>
  );
}
